/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CountryCodeSelect.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Custom country code selection component with search functionality,
    limited display options, and phone number formatting support.

 🔧 Notes:
    - Implements searchable dropdown with 5-item display limit
    - Supports keyboard navigation and phone icon display
    - Includes comprehensive country code options with country names
    - Optimized for performance with filtered results
    - Integrates with form validation systems

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo } from 'react';
import { ChevronDown, Search, Phone } from 'lucide-react';
import { countryCodeOptions } from '../../data/formOptions';

interface CountryCodeSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const CountryCodeSelect: React.FC<CountryCodeSelectProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'Select country code...',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filtrar opciones basado en la búsqueda
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return countryCodeOptions;
    }
    
    const query = searchQuery.toLowerCase();
    return countryCodeOptions.filter(option => 
      option.label.toLowerCase().includes(query) ||
      option.value.includes(query) ||
      // Buscar por nombre del país
      option.label.split('(')[1]?.replace(')', '').toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Obtener la etiqueta del valor seleccionado
  const selectedLabel = useMemo(() => {
    const selected = countryCodeOptions.find(option => option.value === value);
    return selected ? selected.label : placeholder;
  }, [value, placeholder]);

  // Manejar selección
  const handleSelect = (countryCodeValue: string) => {
    onChange(countryCodeValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Manejar teclado
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-border'}
          ${className}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate text-left">
            {selectedLabel}
          </span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search country codes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                No country codes found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`
                    w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                    flex items-center gap-2
                    ${value === option.value ? 'bg-primary/10 text-primary' : 'text-foreground'}
                  `}
                >
                  <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">{option.label}</span>
                </button>
              ))
            )}
          </div>

          {/* Footer with count */}
          {filteredOptions.length > 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border bg-muted/30">
              {filteredOptions.length} countr{filteredOptions.length !== 1 ? 'ies' : 'y'} found
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
        />
      )}
    </div>
  );
};

export default CountryCodeSelect;
